import { universalFields } from '../_shared/universal.js';
/**
 * cod-buybox — the conversion centerpiece of the product page. An interactive buy-box supporting two
 * modes: `cod` (an inline cash-on-delivery order form + a single big CTA) and `cart` (quantity +
 * add-to-cart). The COD-form group is hidden via `visibleWhen` when mode=cart, and the custom-CTA-color
 * field is hidden unless ctaColor=custom — both exercise the deep-config conditional-visibility infra.
 *
 * Seeded blocks: 3 `benefit` bullets, 4 `codField` form fields (Full name / Phone[req] / City /
 * Address), 3 `trust` chips — supplied via the preset so a freshly-inserted section is immediately
 * useful.
 */
export const codBuyboxSchema = {
    type: 'cod-buybox',
    name: 'COD buy-box',
    version: 1,
    templates: ['product'],
    fields: [
        // ── Mode ─────────────────────────────────────────────────────────────────
        {
            id: 'mode',
            type: 'select',
            label: 'Buy mode',
            group: 'Mode',
            info: 'COD shows an inline order form; Cart adds a quantity + add-to-cart button.',
            default: 'cod',
            options: [
                { value: 'cod', label: 'Cash on delivery (inline form)' },
                { value: 'cart', label: 'Add to cart' },
            ],
        },
        // ── Rating ───────────────────────────────────────────────────────────────
        {
            id: 'showRating',
            type: 'checkbox',
            label: 'Show rating',
            group: 'Rating',
            info: 'A star rating + review count under the title (social proof).',
            default: true,
        },
        {
            id: 'ratingValue',
            type: 'range',
            label: 'Rating value',
            group: 'Rating',
            min: 0,
            max: 5,
            step: 0.5,
            default: 4.5,
            visibleWhen: { field: 'showRating', equals: true },
        },
        {
            id: 'ratingCount',
            type: 'number',
            label: 'Review count',
            group: 'Rating',
            default: 128,
            min: 0,
            max: 100000,
            step: 1,
            visibleWhen: { field: 'showRating', equals: true },
        },
        // ── Price ────────────────────────────────────────────────────────────────
        {
            id: 'showCompareAt',
            type: 'checkbox',
            label: 'Show compare-at price',
            group: 'Price',
            default: true,
        },
        {
            id: 'savingsFormat',
            type: 'select',
            label: 'Savings',
            group: 'Price',
            default: 'amount',
            options: [
                { value: 'amount', label: 'Amount saved' },
                { value: 'percent', label: 'Percent off' },
                { value: 'none', label: 'Hide' },
            ],
        },
        {
            id: 'savingsTone',
            type: 'select',
            label: 'Savings tone',
            group: 'Price',
            default: 'calm',
            options: [
                { value: 'calm', label: 'Calm (accent)' },
                { value: 'urgent', label: 'Urgent (red)' },
            ],
        },
        {
            id: 'priceSuffix',
            type: 'text',
            label: 'Price suffix',
            group: 'Price',
            placeholder: 'incl. tax',
            maxLength: 24,
        },
        // ── Variants ─────────────────────────────────────────────────────────────
        {
            id: 'variantStyle',
            type: 'select',
            label: 'Variant style',
            group: 'Variants',
            default: 'auto',
            options: [
                { value: 'auto', label: 'Auto (swatch if colour, else pills)' },
                { value: 'swatch', label: 'Swatches' },
                { value: 'pills', label: 'Pills' },
                { value: 'dropdown', label: 'Dropdown' },
            ],
        },
        {
            id: 'showSizeGuide',
            type: 'checkbox',
            label: 'Show size guide link',
            group: 'Variants',
            default: false,
        },
        {
            id: 'sizeGuideLabel',
            type: 'text',
            label: 'Size guide label',
            group: 'Variants',
            default: 'Guide des tailles',
            defaultKey: 'product.size_guide',
            maxLength: 32,
            visibleWhen: { field: 'showSizeGuide', equals: true },
        },
        // ── Benefits ─────────────────────────────────────────────────────────────
        // (benefit bullets are blocks — see `blocks` below)
        // ── COD form (hidden when mode=cart) ─────────────────────────────────────
        {
            id: 'codFormTitle',
            type: 'text',
            label: 'Form title',
            group: 'COD form',
            default: 'Vos coordonnées de livraison',
            defaultKey: 'cod.form_title',
            maxLength: 60,
            visibleWhen: { field: 'mode', equals: 'cod' },
        },
        // ── CTA ──────────────────────────────────────────────────────────────────
        {
            id: 'ctaLabel',
            type: 'text',
            label: 'Button label',
            group: 'CTA',
            default: 'Commander — paiement à la livraison',
            defaultKey: 'checkout.order_cod_cta',
            maxLength: 60,
        },
        {
            id: 'ctaColor',
            type: 'select',
            label: 'Button color',
            group: 'CTA',
            default: 'theme',
            options: [
                { value: 'theme', label: 'Theme accent' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'ctaCustomColor',
            type: 'color',
            label: 'Custom button color',
            group: 'CTA',
            default: { ref: 'custom', value: '#0a7c5a' },
            visibleWhen: { field: 'ctaColor', equals: 'custom' },
        },
        {
            id: 'showQuantity',
            type: 'checkbox',
            label: 'Show quantity selector',
            group: 'CTA',
            info: 'A quantity stepper above the button (both COD and cart modes).',
            default: true,
        },
        {
            id: 'ctaUppercase',
            type: 'checkbox',
            label: 'Uppercase button text',
            group: 'CTA',
            default: false,
        },
        {
            id: 'showBuyItNow',
            type: 'checkbox',
            label: 'Show “Buy it now” button',
            group: 'CTA',
            info: 'A secondary dynamic-checkout button under add-to-cart (cart mode only).',
            default: false,
            visibleWhen: { field: 'mode', equals: 'cart' },
        },
        {
            id: 'buyItNowLabel',
            type: 'text',
            label: '“Buy it now” label',
            group: 'CTA',
            default: 'Acheter maintenant',
            defaultKey: 'product.buy_now',
            maxLength: 40,
            visibleWhen: { field: 'showBuyItNow', equals: true },
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        // `width` is intentionally dropped: the buy-box is a fixed ~560px reading column (see BuyboxBody), so a
        // Narrow/Standard/Wide/Full control would do nothing here. Same pattern as slideshow/collection-banner.
        ...universalFields.filter((f) => f.id !== 'width'),
    ],
    blocks: [
        {
            type: 'benefit',
            name: 'Benefit',
            max: 6,
            fields: [
                { id: 'icon', type: 'icon', label: 'Icon', default: 'check-circle' },
                { id: 'text', type: 'text', label: 'Text', default: 'Avantage', maxLength: 80 },
            ],
        },
        {
            type: 'codField',
            name: 'Order field',
            max: 8,
            fields: [
                {
                    id: 'fieldType',
                    type: 'select',
                    label: 'Field',
                    default: 'name',
                    options: [
                        { value: 'name', label: 'Full name' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'city', label: 'City' },
                        { value: 'address', label: 'Address' },
                        { value: 'note', label: 'Note' },
                    ],
                },
                { id: 'label', type: 'text', label: 'Label', default: 'Champ', maxLength: 40 },
                { id: 'required', type: 'checkbox', label: 'Required', default: false },
            ],
        },
        {
            type: 'trust',
            name: 'Trust chip',
            max: 4,
            fields: [
                { id: 'icon', type: 'icon', label: 'Icon', default: 'shield-check' },
                { id: 'text', type: 'text', label: 'Text', default: 'Garantie', maxLength: 60 },
            ],
        },
    ],
    maxBlocks: 18,
};
export const codBuyboxPresets = [
    {
        name: 'COD buy-box',
        settings: {
            mode: 'cod',
            showCompareAt: true,
            savingsFormat: 'amount',
            savingsTone: 'calm',
            // `ctaLabel` was repeated here, identical to the field's own default. A preset is spread OVER the
            // defaults, so the copy would have overwritten the translated one and put French back into an Arabic
            // shop. Removed: the field's `defaultKey` is now the only source for this label.
        },
        // `@t:` marks a sentence the preset wants TRANSLATED at insert time — see applyDefaults.
        blocks: [
            {
                type: 'benefit',
                settings: { icon: 'check-circle', text: '@t:cod.benefit_premium_material' },
            },
            { type: 'benefit', settings: { icon: 'package', text: '@t:cod.benefit_fast_shipping' } },
            { type: 'benefit', settings: { icon: 'return', text: '@t:cod.benefit_easy_exchange' } },
            {
                type: 'codField',
                settings: { fieldType: 'name', label: '@t:cod.default_field_name', required: true },
            },
            {
                type: 'codField',
                settings: { fieldType: 'phone', label: '@t:cod.default_field_phone', required: true },
            },
            {
                type: 'codField',
                settings: { fieldType: 'city', label: '@t:cod.default_field_city', required: false },
            },
            {
                type: 'codField',
                settings: { fieldType: 'address', label: '@t:cod.default_field_address', required: false },
            },
            { type: 'trust', settings: { icon: 'cash', text: '@t:checkout.pay_on_delivery' } },
            { type: 'trust', settings: { icon: 'return', text: '@t:cod.trust_returns_14_days' } },
            { type: 'trust', settings: { icon: 'delivery', text: '@t:cod.trust_ships_everywhere' } },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const codBuyboxMeta = {
    label: 'COD buy-box',
    group: 'Product',
    icon: 'cash',
};
//# sourceMappingURL=schema.js.map